import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react'
// import { forwardRef } from "react"
import SEO from '../SEO'
import { generateGuidelineSchema } from '../../services/schemaGenerators'
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp'
import {
  Box,
  IconButton,
  Typography,
  Paper,
  Skeleton,
  Fab,
  Zoom,
  useTheme,
  useMediaQuery,
  Tooltip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material'
import PrintRoundedIcon from '@mui/icons-material/PrintRounded'
import MailRoundedIcon from '@mui/icons-material/MailRounded'
import OpenInNewRoundedIcon from '@mui/icons-material/OpenInNewRounded'
import ReportRoundedIcon from '@mui/icons-material/ReportRounded'
import CloseRoundedIcon from '@mui/icons-material/CloseRounded'
import SortByAlphaRoundedIcon from '@mui/icons-material/SortByAlphaRounded'
import RestoreRoundedIcon from '@mui/icons-material/RestoreRounded'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import { ShareRounded } from '@mui/icons-material'
import { Enhanced404Handler } from '../Error404'
import { fetchGuideBySlug } from '../../services/articleAPISevicesUsingFetch'
import apiService from '../../services/ApiService'
import BetaVersionNotice from '../BetaVersionNotice'
import {
  handleEmail,
  handleOpenInNewTab,
} from '../../services/articleCardHandlers'

import { parseHtmlSanitizeAddTargetToLinks } from '../../services/utils'
import FeedbackForm from '../FeedbackForm'

// Add a cache at module level to persist between renders and component instances
const contentCache = {}

const GuideArticle = ({
  article,
  searchQuery,
  showSnackbar,
  isInModal = false, // Prop to indicate if component is inside a modal
  parentContainerRef = null, // Reference to parent container for scrolling in modal mode
}) => {
  const [guideContent, setGuideContent] = useState(null)
  const [loading, setLoading] = useState(true) // The loading state if initially set to false, then set to true when fetching begins. Be aware it might briefly show "No content available" before showing the loading skeleton.
  const [error, setError] = useState(null)
  const [isFeedbackFormOpen, setIsFeedbackFormOpen] = useState(false)
  const [showBackToTop, setShowBackToTop] = useState(false)
  const componentRef = useRef()
  const fetchedRef = useRef(false) // Use to track if we've already fetched

  // we need to sort lexicographically, all the definitions of Article 1 of CIT law
  const [parsedContent, setParsedContent] = useState(null)
  const [sortedContent, setSortedContent] = useState(null)
  const [contentHtml, setContentHtml] = useState(null)
  const [gtlNotesContent, setGtlNotesContent] = useState(null)
  const [gtlNotesTitle, setGtlNotesTitle] = useState('GTL Notes')
  const [isAccordionExpanded, setIsAccordionExpanded] = useState(false)
  const themeColors = {
    primary: '#232536',
    secondary: '#ffcf51',
    hover: '#3a3e5a',
  }
  const theme = useTheme()
  const isMediumScreen = useMediaQuery(theme.breakpoints.down('md'))
  const isMobileScreen = useMediaQuery(theme.breakpoints.down('sm'))
  const buttonBorderRadius = '28px'

  // Decide which container to use for scroll detection and scrollToTop action
  const scrollContainer = isInModal ? parentContainerRef : window
  const extractGtlNotes = useCallback((htmlContent) => {
    try {
      if (!htmlContent) return null

      const parser = new DOMParser()
      const doc = parser.parseFromString(htmlContent, 'text/html')

      const allGtlHeadings = doc.querySelectorAll('h3.linktogtl')
      let gtlHeading = null

      for (const heading of allGtlHeadings) {
        const anchorTag = heading.querySelector('a')
        if (anchorTag) {
          const text = anchorTag.textContent.trim()
          const id = anchorTag.id

          if (text.startsWith('GTL Notes') && id === 'bookmark') {
            gtlHeading = heading
            break
          } else if (
            text.includes('GTL Notes') &&
            !text.includes('[') &&
            !text.includes(']')
          ) {
            gtlHeading = heading
            break
          }
        }
      }

      if (!gtlHeading) {
        return null
      }

      const anchorTag = gtlHeading.querySelector('a')
      const title = anchorTag ? anchorTag.textContent.trim() : 'GTL Notes'

      let currentElement = gtlHeading.nextElementSibling
      const gtlContent = []

      while (currentElement) {
        if (currentElement.tagName === 'H3') {
          break
        }
        gtlContent.push(currentElement.outerHTML)
        currentElement = currentElement.nextElementSibling
      }
      return gtlContent.length > 0
        ? {
            title,
            content: gtlContent.join(''),
          }
        : null
    } catch (error) {
      console.warn('Error extracting GTL Notes:', error)
      return null
    }
  }, [])

  const removeGtlNotesFromContent = useCallback((htmlContent) => {
    if (!htmlContent) return htmlContent

    const parser = new DOMParser()
    const doc = parser.parseFromString(htmlContent, 'text/html')

    const allGtlHeadings = doc.querySelectorAll('h3.linktogtl')
    let gtlHeading = null

    for (const heading of allGtlHeadings) {
      const anchorTag = heading.querySelector('a')
      if (anchorTag) {
        const text = anchorTag.textContent.trim()
        const id = anchorTag.id

        if (text.startsWith('GTL Notes') && id === 'bookmark') {
          gtlHeading = heading
          break
        } else if (
          text.includes('GTL Notes') &&
          !text.includes('[') &&
          !text.includes(']')
        ) {
          gtlHeading = heading
          break
        }
      }
    }

    if (gtlHeading) {
      const elementsToRemove = [gtlHeading]

      let currentElement = gtlHeading.nextElementSibling
      while (currentElement) {
        if (currentElement.tagName === 'H3') {
          break
        }
        elementsToRemove.push(currentElement)
        currentElement = currentElement.nextElementSibling
      }

      elementsToRemove.forEach((el) => el.remove())
    }

    return doc.body.innerHTML
  }, [])
  const memoizedGtlNotes = useMemo(() => {
    return guideContent?.content ? extractGtlNotes(guideContent.content) : null
  }, [guideContent?.content, extractGtlNotes])

  // Show back to top button when scrolled down
  useEffect(() => {
    if (!scrollContainer) return

    const handleScroll = () => {
      // For window, use scrollY; for element, use scrollTop
      const scrollPosition = isInModal
        ? scrollContainer.current?.scrollTop
        : window.scrollY

      if (scrollPosition > 300) {
        setShowBackToTop(true)
      } else {
        setShowBackToTop(false)
      }
    }

    // Add scroll listener to the appropriate container
    if (isInModal && scrollContainer.current) {
      scrollContainer.current.addEventListener('scroll', handleScroll)
    } else {
      window.addEventListener('scroll', handleScroll)
    }

    return () => {
      // Clean up scroll listener
      if (isInModal && scrollContainer.current) {
        scrollContainer.current.removeEventListener('scroll', handleScroll)
      } else {
        window.removeEventListener('scroll', handleScroll)
      }
    }
  }, [isInModal, scrollContainer])

  // Scroll to top function
  const scrollToTop = useCallback(() => {
    if (isInModal && parentContainerRef?.current) {
      parentContainerRef.current.scrollTo({
        top: 0,
        behavior: 'smooth',
      })
    } else {
      window.scrollTo({
        top: 0,
        behavior: 'smooth',
      })
    }
  }, [isInModal, parentContainerRef])

  // Load external CSS
  useEffect(() => {
    const environment = process.env.REACT_APP_ENVIRONMENT || 'dev'
    const link = document.createElement('link')
    link.rel = 'stylesheet'
    link.href = `https://gtlcdn-eufeh8ffbvbvacgf.z03.azurefd.net/guide/stylesheets/${environment}/guide.css`
    link.id = 'external-css'

    if (!document.getElementById('external-css')) {
      document.head.appendChild(link)
    }

    return () => {
      document.getElementById('external-css')?.remove() // Cleanup on unmount
    }
  }, [])

  // SEO Data Generation
  const generateSEOData = useCallback(() => {
    if (!guideContent) return null

    // Generate title based on guide data
    const seoTitle = guideContent.title
      ? `${guideContent.title.replace(/<[^>]*>/g, '')} | ${
          guideContent.type || 'FTA Guide'
        } | GCC Tax Laws`
      : `${
          guideContent.uniqueCode || 'FTA Guide'
        } | UAE Tax Guidance | GCC Tax Laws`

    // Generate description from content
    const seoDescription =
      guideContent?.summary ||
      (guideContent?.textOnly || guideContent?.text
        ? (guideContent?.textOnly || guideContent?.text).substring(0, 155) +
          '...'
        : `Official FTA guidance ${
            guideContent.uniqueCode || ''
          } providing detailed tax compliance information and procedures.`)

    // Generate canonical URL
    const canonicalUrl = `https://gcctaxlaws.com/guidances/${guideContent.slug}`

    // Generate keywords
    const keywords = [
      'UAE tax guidance',
      'FTA guidance',
      guideContent?.type?.toLowerCase(),
      guideContent?.uniqueCode,
      'tax compliance',
      guideContent?.lawSlug ? guideContent?.lawSlug.replace('-', ' ') : null,
    ]
      .filter(Boolean)
      .join(', ')

    // Generate schema markup
    const schemaMarkup = generateGuidelineSchema(guideContent)

    return {
      title: seoTitle,
      description: seoDescription,
      canonical: canonicalUrl,
      keywords,
      schemaMarkup,
      publishedDate: guideContent?.createdAt,
      modifiedDate: guideContent?.updatedAt,
      guideType: guideContent?.type,
      uniqueCode: guideContent?.uniqueCode,
    }
  }, [guideContent])

  // Fetch guide content with caching
  useEffect(() => {
    const getGuideContent = async () => {
      // If we already have full article data, no need to fetch again
      if (article?.content) {
        setGuideContent(article)
        setLoading(false)
        return
      }

      // Skip if no slug or already fetching
      if (!article?.slug || fetchedRef.current) {
        return
      }
      // Check module-level cache first
      if (contentCache[article.slug]) {
        // console.log('Using cached content for', article.slug)
        setGuideContent(contentCache[article.slug])
        setLoading(false)
        return
      }
      // console.log('Fetching content for', article.slug)
      setLoading(true)
      fetchedRef.current = true
      try {
        // const data = await fetchGuideBySlug(article.slug)
        const data = await apiService.getGuidelineBySlug(article.slug)
        // Store in module-level cache
        contentCache[article.slug] = data
        setGuideContent(data)
        setError(null)
      } catch (err) {
        console.error('Error fetching guide content:', err.message)
        setError(err.message)
        fetchedRef.current = false // Reset in case of error to allow retry
      } finally {
        setLoading(false)
      }
    }
    getGuideContent()
    // Cleanup function to reset fetch state if component unmounts
    return () => {
      fetchedRef.current = false
    }
  }, [article?.slug])

  // Generate SEO data
  const seoData = generateSEOData()

  // Parse HTML content when article content changes, so we can check whether it contains any tables or not, if yes then we can show sort button
  useEffect(() => {
    if (guideContent?.content) {
      // Parse the HTML content
      const parser = new DOMParser()
      const doc = parser.parseFromString(guideContent.content, 'text/html')
      setParsedContent(doc)
      setSortedContent(null) // Reset sorted content when article changes

      // Set the initial HTML content
      if (memoizedGtlNotes) {
        setGtlNotesContent(memoizedGtlNotes.content)
        setGtlNotesTitle(memoizedGtlNotes.title)

        const contentWithoutGtl = removeGtlNotesFromContent(
          guideContent.content
        )
        setContentHtml(contentWithoutGtl)
      } else {
        setGtlNotesContent(null)
        setContentHtml(guideContent.content)
      }
    }
  }, [guideContent?.content, memoizedGtlNotes, removeGtlNotesFromContent])

  // below function is used to sort all the tables, we just need to sort the table where definitions are there in the article content
  const sortDefinitions = useCallback(() => {
    if (!parsedContent) return

    // Create a deep clone of the parsed content
    const baseContent = memoizedGtlNotes
      ? removeGtlNotesFromContent(guideContent?.content || '')
      : guideContent?.content || ''

    const parser = new DOMParser()
    const doc = parser.parseFromString(baseContent, 'text/html')
    const docClone = doc.cloneNode(true)

    // Find all tables in the document
    // const tables = docClone.querySelectorAll('table')

    // Find only tables with class="definition"
    const tables = docClone.querySelectorAll('table.definition')

    tables.forEach((table) => {
      // Get all rows except header row (if any)
      const rows = Array.from(table.querySelectorAll('tr')).slice(1)

      if (rows.length > 0) {
        // Sort rows by the text in the first column
        rows.sort((a, b) => {
          const aText = a.querySelector('td:first-child')?.textContent || ''
          const bText = b.querySelector('td:first-child')?.textContent || ''
          return aText.localeCompare(bText)
        })

        // Remove existing rows
        rows.forEach((row) => row.parentNode.removeChild(row))

        // Append sorted rows
        const tbody = table.querySelector('tbody') || table
        rows.forEach((row) => tbody.appendChild(row))
      }
    })

    setSortedContent(docClone)
    setContentHtml(docClone.body.innerHTML)

    if (showSnackbar) {
      showSnackbar('Definitions sorted alphabetically', 'success')
    }
  }, [
    parsedContent,
    showSnackbar,
    memoizedGtlNotes,
    guideContent,
    removeGtlNotesFromContent,
  ])

  // Reset to original content
  const resetToDefault = useCallback(() => {
    setSortedContent(null)
    const baseContent = memoizedGtlNotes
      ? removeGtlNotesFromContent(guideContent?.content || '')
      : guideContent?.content || ''

    setContentHtml(baseContent)
    // Provide feedback when resetting
    if (showSnackbar) {
      showSnackbar('Restored original order', 'info')
    }
  }, [guideContent, showSnackbar, memoizedGtlNotes, removeGtlNotesFromContent])

  const handleFeedbackFormToggle = useCallback(() => {
    setIsFeedbackFormOpen((prev) => !prev)
  }, [])
  const handleShare = useCallback(async () => {
    const url = `${window.location.origin}/guidances/${
      guideContent?.slug || article?.slug
    }`
    const title = guideContent?.title || article?.title || 'Article'

    // Remove HTML tags from title for clean sharing
    const cleanTitle = title.replace(/<[^>]*>/g, '')

    const shareData = {
      title: cleanTitle,
      text: `Check out this article: ${cleanTitle}`,
      url: url,
    }

    try {
      // Check if Web Share API is supported (mainly mobile)
      if (navigator.share) {
        await navigator.share(shareData)
        if (showSnackbar) {
          showSnackbar('Article shared successfully! 📤', 'success')
        }
      } else {
        // Fallback to clipboard copy
        await navigator.clipboard.writeText(url)
        if (showSnackbar) {
          showSnackbar('Article link copied to clipboard! 📋', 'success')
        }
      }
    } catch (error) {
      if (error.name !== 'AbortError') {
        // Fallback to clipboard if share fails
        try {
          await navigator.clipboard.writeText(url)
          if (showSnackbar) {
            showSnackbar('Article link copied to clipboard! 📋', 'success')
          }
        } catch (clipboardError) {
          if (showSnackbar) {
            showSnackbar('Failed to share article', 'error')
          }
        }
      }
    }
  }, [guideContent, article, showSnackbar])
  const handlePrint = useCallback(() => {
    if (componentRef.current) {
      const wasAccordionExpanded = isAccordionExpanded

      // If GTL Notes exist and accordion is closed, expand it

      if (gtlNotesContent && !isAccordionExpanded) {
        setIsAccordionExpanded(true)

        // Wait for accordion to expand before printing

        setTimeout(() => {
          performPrint()

          // Restore original accordion state after printing

          setIsAccordionExpanded(wasAccordionExpanded)
        }, 300) // Give accordion time to expand
      } else {
        // No GTL Notes or accordion already expanded, print immediately

        performPrint()
      }
    }

    function performPrint() {
      const printWindow = window.open('', '_blank')
      printWindow.document.write(
        '<html><head><title>GCC Tax Laws - FTA Guidance</title>'
      )

      // Copy all styles from the current document to the print document
      const styleSheets = document.styleSheets
      for (let i = 0; i < styleSheets.length; i++) {
        if (styleSheets[i].href) {
          printWindow.document.write(
            `<link rel="stylesheet" type="text/css" href="${styleSheets[i].href}">`
          )
        } else {
          try {
            let css = ''
            for (let j = 0; j < styleSheets[i].cssRules.length; j++) {
              css += styleSheets[i].cssRules[j].cssText
            }
            printWindow.document.write(`<style>${css}</style>`)
          } catch (e) {
            console.warn('Error accessing stylesheet', e)
          }
        }
      }

      printWindow.document.write('</head><body>')
      printWindow.document.write(componentRef.current.innerHTML)
      printWindow.document.write('</body></html>')
      printWindow.document.close()
      printWindow.print()
      // Attach the onafterprint event to close the print window
      printWindow.onafterprint = () => {
        printWindow.close()
      }
    }
  }, [gtlNotesContent, isAccordionExpanded])

  const hasDefinitionTables =
    parsedContent?.querySelectorAll('table.definition').length > 0

  if (loading) {
    return (
      <Box sx={{ padding: 2, margin: 2 }}>
        <Skeleton variant='rectangular' width='100%' height={200} />
        <Skeleton variant='text' width='60%' />
        <Skeleton variant='text' width='80%' />
      </Box>
    )
  }
  if (error) {
    return (
      <Enhanced404Handler
        error={error}
        article={article}
        showFullDetails={process.env.REACT_APP_ENVIRONMENT === 'dev'} // Show details only in dev
      />
    )
  }

  return (
    <>
      {/* SEO Component - Only add if not in modal and we have content */}
      {!isInModal && seoData && (
        <SEO
          title={seoData.title}
          description={seoData.description}
          canonical={seoData.canonical}
          keywords={seoData.keywords}
          schemaMarkup={seoData.schemaMarkup}
          publishedDate={seoData.publishedDate}
          modifiedDate={seoData.modifiedDate}
          guideType={seoData.guideType}
          uniqueCode={seoData.uniqueCode}
          articleType='guidance'
        />
      )}
      {guideContent?.betaVersion && (
        <BetaVersionNotice
          show={guideContent.betaVersion}
          isInModal={isInModal}
        />
      )}
      <Paper
        ref={componentRef}
        sx={{ padding: 2, margin: 2, borderRadius: '8px' }}
      >
        <Box display='flex' justifyContent='flex-end' alignItems='center'>
          <Box>
            {hasDefinitionTables && (
              <Tooltip
                title={
                  sortedContent
                    ? 'Restore the order of definitions back'
                    : 'Sort Definitions Alphabetically'
                }
                arrow
              >
                <IconButton
                  aria-label={
                    sortedContent
                      ? 'Restore the order of definitions back'
                      : 'Sort Definitions Alphabetically'
                  }
                  onClick={sortedContent ? resetToDefault : sortDefinitions}
                  size={isMobileScreen ? 'small' : 'medium'}
                >
                  {sortedContent ? (
                    <RestoreRoundedIcon sx={{ color: themeColors.primary }} />
                  ) : (
                    <SortByAlphaRoundedIcon
                      sx={{ color: themeColors.primary, fontSize: 28 }}
                    />
                  )}
                </IconButton>
              </Tooltip>
            )}
            <Tooltip
              title={
                isFeedbackFormOpen
                  ? 'Close feedback form'
                  : 'Report Broken Links in this Article'
              }
              arrow
            >
              <IconButton
                aria-label={
                  isFeedbackFormOpen
                    ? 'Close feedback form'
                    : 'Report Broken Links in this FTA Guidance'
                }
                onClick={handleFeedbackFormToggle}
                size={isMobileScreen ? 'small' : 'medium'}
              >
                {isFeedbackFormOpen ? (
                  <CloseRoundedIcon color='error' />
                ) : (
                  <ReportRoundedIcon color='error' />
                )}
              </IconButton>
            </Tooltip>
            <Tooltip title='Print this article' arrow>
              <IconButton
                aria-label='Print this FTA Guidance'
                // title='Print this FTA Guidance'
                onClick={handlePrint}
                size={isMobileScreen ? 'small' : 'medium'}
              >
                <PrintRoundedIcon sx={{ color: themeColors.primary }} />
              </IconButton>
            </Tooltip>
            <Tooltip title='Share this article' arrow>
              <IconButton
                aria-label='Share this article'
                onClick={handleShare}
                size={isMobileScreen ? 'small' : 'medium'}
              >
                <ShareRounded sx={{ color: themeColors.primary }} />
              </IconButton>
            </Tooltip>
            <Tooltip title='Mail this article' arrow>
              <IconButton
                aria-label='Mail this FTA Guidance'
                // title='Mail this FTA Guidance'
                onClick={() => handleEmail(guideContent || article)}
                size={isMobileScreen ? 'small' : 'medium'}
              >
                <MailRoundedIcon sx={{ color: themeColors.primary }} />
              </IconButton>
            </Tooltip>
            <Tooltip title='Open this article in new tab' arrow>
              <IconButton
                aria-label='Open this FTA Guidance in new tab'
                // title='Open this FTA Guidance in new tab'
                onClick={() =>
                  handleOpenInNewTab(
                    guideContent || article,
                    `/guidances/${article?.slug}`,
                    showSnackbar
                  )
                }
                size={isMobileScreen ? 'small' : 'medium'}
              >
                {/*fontSize: (default 24px), OR large, (default medium), small, string */}
                <OpenInNewRoundedIcon sx={{ color: themeColors.primary }} />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        {isFeedbackFormOpen && (
          <FeedbackForm
            articleId={guideContent?.slug || article?.slug}
            onClose={handleFeedbackFormToggle}
            showSnackbar={showSnackbar}
          />
        )}

        {contentHtml ? (
          <Box mt={2}>
            {parseHtmlSanitizeAddTargetToLinks(
              contentHtml,
              searchQuery,
              '/guidances/' + (guideContent?.slug || article?.slug),
              {
                fullDocumentContent: guideContent?.content || contentHtml,
                documentTitle:
                  guideContent?.title || article?.title || 'Guideline',
                showSnackbar: showSnackbar,
              }
            )}
            {gtlNotesContent && (
              <Box mt={3}>
                <Accordion
                  expanded={isAccordionExpanded}
                  onChange={(event, expanded) =>
                    setIsAccordionExpanded(expanded)
                  }
                  aria-label='GTL Notes section'
                  className='gtl-notes-accordion'
                  sx={{
                    '&:before': {
                      display: 'none',
                    },
                    boxShadow: 'none',
                    border: `1px solid ${theme.palette.divider}`,
                    borderRadius: '8px !important',
                    '&:not(:last-child)': {
                      borderBottom: 0,
                    },
                  }}
                >
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    className='gtl-notes-accordion-summary'
                    sx={{
                      backgroundColor:
                        theme.palette.mode === 'dark'
                          ? 'rgba(255, 255, 255, 0.05)'
                          : 'rgba(0, 0, 0, 0.03)',
                      borderRadius: '8px',
                      '&.Mui-expanded': {
                        borderBottomLeftRadius: 0,
                        borderBottomRightRadius: 0,
                      },
                    }}
                  >
                    <Typography
                      variant='h6'
                      sx={{
                        fontWeight: 600,
                        color: '#43af76',
                      }}
                    >
                      {gtlNotesTitle}
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails
                    sx={{ padding: 2 }}
                    className='gtl-notes-accordion-details'
                  >
                    <Box className='gtl-notes-content'>
                      {parseHtmlSanitizeAddTargetToLinks(
                        gtlNotesContent,
                        searchQuery,

                        '/guidances/' + (guideContent?.slug || article?.slug),
                        {
                          fullDocumentContent:
                            guideContent?.content || gtlNotesContent,
                          documentTitle:
                            guideContent?.title ||
                            article?.title ||
                            'Guideline',
                          showSnackbar: showSnackbar,
                        }
                      )}
                    </Box>
                  </AccordionDetails>
                </Accordion>
              </Box>
            )}
          </Box>
        ) : (
          <Typography>No content available.</Typography>
        )}

        {/* Back to Top button - only shown in standalone mode (non-modal), not in modal */}
        {!isInModal && (
          <Box
            sx={{
              position: 'fixed',
              bottom: 20,
              right: 20,
              zIndex: 1000,
            }}
          >
            <Zoom in={showBackToTop}>
              {isMediumScreen ? (
                <Fab
                  size='small'
                  aria-label='Scroll back to top'
                  onClick={scrollToTop}
                  sx={{
                    backgroundColor: themeColors.primary,
                    color: 'white',
                    '&:hover': {
                      backgroundColor: themeColors.hover,
                    },
                  }}
                >
                  <KeyboardArrowUpIcon fontSize='small' />
                </Fab>
              ) : (
                <Fab
                  variant='extended'
                  size='medium'
                  aria-label='Scroll back to top'
                  onClick={scrollToTop}
                  sx={{
                    backgroundColor: themeColors.primary,
                    color: 'white',
                    '&:hover': { backgroundColor: themeColors.hover },
                    paddingLeft: 2,
                    paddingRight: 2,
                    borderRadius: buttonBorderRadius,
                  }}
                >
                  <KeyboardArrowUpIcon sx={{ mr: 1 }} />
                  <Typography
                    variant='button'
                    sx={{
                      fontWeight: 'bold',
                    }}
                  >
                    Back to Top
                  </Typography>
                </Fab>
              )}
            </Zoom>
          </Box>
        )}
      </Paper>
    </>
  )
}

export default GuideArticle
